/**
 * How much a bullet hurts: range, hit location, and the weapon it came from.
 *
 * Modelled on Modern Warfare's structure, which is the thing worth copying —
 * the exact figures are a balance dial, the SHAPE is what makes gunfights read
 * correctly:
 *
 *   - Damage is flat out to a near range, falls off linearly to a far range,
 *     and is flat again beyond it. Not a single number, and not a curve that
 *     keeps dropping forever.
 *   - Hit location multiplies it. A head is worth much more than a leg, and the
 *     head bonus is per weapon, because a shotgun pellet to the head should not
 *     be worth what a marksman round is.
 *   - Both are what decide an engagement: the same rifle wins a room and loses
 *     a courtyard, and that difference is the whole reason to carry an SMG.
 *
 * The previous model had none of it. Every weapon did one flat number at any
 * distance, with a head multiplier of up to 3.0 — so an SMG hit exactly as hard
 * across Dust II as it did in a doorway, and a headshot was worth three body
 * shots rather than the ~1.4 a real game uses.
 *
 * A NOTE ON THE NUMBERS: they are MW-shaped rather than extracted from it. I do
 * not have its tables to hand, and inventing precise values and calling them
 * canonical would be worse than saying so. The near/far damages and the
 * breakpoints below are chosen to give MW-like times to kill at 100 health;
 * tune them here and both the offline game and the server follow.
 */

/**
 * Where a round landed. The server resolves these from height along the body
 * and the client from which bone capsule was struck, so the set has to be one
 * both can name.
 *
 * Arms are deliberately absent. The server hit-tests against a vertical spine
 * and cannot tell an arm from the chest behind it; giving the client a zone the
 * server can never award would mean the damage you saw and the damage that
 * counted disagreed exactly when someone shielded their body with an arm.
 */
export const ZONES = ['head', 'chest', 'stomach', 'legs'];

/**
 * Multipliers for everything except the head, which is per weapon.
 *
 * Chest and stomach are both 1.0 on purpose: the times to kill below are quoted
 * for a torso hit, and splitting them would mean quoting two. Legs cost you a
 * little, which is what makes shooting low a mistake rather than a style.
 */
export const ZONE_MULTIPLIER = {
  head: 1,        // replaced by the weapon's own headMult
  chest: 1.0,
  stomach: 1.0,
  legs: 0.9,
};

/**
 * Fractions of body height where each zone starts, measured on the actual rig:
 * head bone at 1.537 m, skull to 1.81, chest 1.263, hips 0.975, feet at 0.
 * Expressed as fractions so a crouched or scaled body still divides correctly.
 */
export const ZONE_BANDS = [
  { zone: 'head', from: 0.829 },      // 1.50 m and up on a 1.81 m body
  { zone: 'chest', from: 0.635 },     // 1.15 m
  { zone: 'stomach', from: 0.470 },   // 0.85 m
  { zone: 'legs', from: 0 },
];

/** Which zone a hit at `frac` of the way up the body belongs to. */
export function zoneAtHeight(frac) {
  for (const b of ZONE_BANDS) if (frac >= b.from) return b.zone;
  return 'legs';
}

/**
 * Damage before hit location, at a given distance.
 *
 * Weapons without a profile keep their single number at every range, so
 * anything not yet tuned behaves exactly as it did.
 */
export function damageAtRange(spec, dist) {
  const near = spec.nearRange;
  const far = spec.farRange;
  const dmgMin = spec.damageMin;
  if (near == null || far == null || dmgMin == null || far <= near) return spec.damage;
  if (dist <= near) return spec.damage;
  if (dist >= far) return dmgMin;
  const t = (dist - near) / (far - near);
  return spec.damage + (dmgMin - spec.damage) * t;
}

/** The multiplier for a hit location on a particular weapon. */
export function zoneMultiplier(spec, zone) {
  if (zone === 'head') return spec.headMult ?? 1;
  return ZONE_MULTIPLIER[zone] ?? 1;
}

/** Everything together: what one round does. */
export function bulletDamage(spec, dist, zone) {
  return damageAtRange(spec, dist) * zoneMultiplier(spec, zone);
}

/**
 * A shotgun's pellet pattern — the same one on both sides.
 *
 * Deterministic, and that is the entire point. The client used to scatter
 * pellets randomly while the server fired a single ray and applied one pellet's
 * damage, so a shotgun did a ninth of its damage online and the pattern you
 * watched had no relationship to the one that counted. A fixed pattern means
 * what you see and what the server resolves are the same nine rounds.
 *
 * One down the middle and eight on a ring, which is what a real choke throws
 * and reads far better than noise at close range.
 *
 * @param aim    normalised direction, as {x,y,z}
 * @param spread half-angle of the cone, radians
 * @param count  how many pellets
 * @param write  called with (x, y, z) per pellet
 */
export function pelletPattern(aim, spread, count, write) {
  // Any two axes perpendicular to the aim. Chosen from the world axis the aim
  // is least aligned with, so the frame never degenerates when looking up.
  const ax = Math.abs(aim.y) < 0.9 ? { x: 0, y: 1, z: 0 } : { x: 1, y: 0, z: 0 };
  let rx = ax.y * aim.z - ax.z * aim.y;
  let ry = ax.z * aim.x - ax.x * aim.z;
  let rz = ax.x * aim.y - ax.y * aim.x;
  const rl = Math.hypot(rx, ry, rz) || 1;
  rx /= rl; ry /= rl; rz /= rl;
  const ux = aim.y * rz - aim.z * ry;
  const uy = aim.z * rx - aim.x * rz;
  const uz = aim.x * ry - aim.y * rx;

  const ring = Math.max(0, count - 1);
  for (let i = 0; i < count; i++) {
    let a = 0, b = 0;
    if (i > 0) {
      const th = ((i - 1) / ring) * Math.PI * 2;
      // Slightly inside the full cone: a pattern whose every pellet sits on the
      // rim reads as a smoke ring rather than a shot.
      a = Math.cos(th) * spread * 0.85;
      b = Math.sin(th) * spread * 0.85;
    }
    const x = aim.x + rx * a + ux * b;
    const y = aim.y + ry * a + uy * b;
    const z = aim.z + rz * a + uz * b;
    const l = Math.hypot(x, y, z) || 1;
    write(x / l, y / l, z / l);
  }
}
